import { useEffect, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, RefreshCw, Send, Database } from "lucide-react";
import { toast } from "sonner";

function Md({ text }) {
  return (
    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed"
         dangerouslySetInnerHTML={{ __html: (text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;")
           .replace(/\*\*(.+?)\*\*/g, "<strong class='text-slate-900'>$1</strong>") }} />
  );
}

export function AIPanel({ screen }) {
  const { client } = useCapital();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (refresh = false) => {
    setBusy(true);
    api.get("/capital/ai/analysis", { params: { client, screen, refresh }, timeout: 120000 })
      .then((r) => setData(r.data))
      .catch((e) => refresh && toast.error(e.response?.data?.detail || "AI unavailable"))
      .finally(() => setBusy(false));
  };
  useEffect(() => { if (client) load(); /* eslint-disable-next-line */ }, [client, screen]);
  return (
    <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/40 p-4" data-testid={`ai-panel-${screen}`}>
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-violet-600" />
        <span className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold">AI analysis</span>
        {data?.generated_at && <span className="text-[10px] text-slate-400">{data.generated_at.slice(0, 16).replace("T", " ")}</span>}
        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] ml-auto" onClick={() => load(true)} disabled={busy}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Refresh
        </Button>
      </div>
      {busy && !data ? <div className="text-xs text-slate-400 mt-2">Analysing…</div>
        : data?.text ? <div className="mt-2"><Md text={data.text} /></div>
        : <div className="text-xs text-slate-400 mt-2">Click Refresh to generate analysis.</div>}
    </div>
  );
}

const SAMPLES = [
  "If we only have $150,000 next year, what should we replace?",
  "Why is our immediate attention budget so high?",
  "Which sites carry the most renewal risk?",
  "What data should we improve first?",
];

export function AskAssetAI() {
  const { client } = useCapital();
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dq, setDq] = useState(null);
  const [dqBusy, setDqBusy] = useState(false);

  const ask = async (question) => {
    if (!question.trim()) return;
    setBusy(true);
    setAnswer(null);
    try {
      const r = await api.post("/capital/ai/ask", { client, question }, { timeout: 120000 });
      setAnswer(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI unavailable");
    } finally { setBusy(false); }
  };

  const loadDq = (refresh = false) => {
    setDqBusy(true);
    api.get("/capital/ai/data-quality", { params: { client, refresh }, timeout: 120000 })
      .then((r) => setDq(r.data)).catch(() => {}).finally(() => setDqBusy(false));
  };
  useEffect(() => { if (client) loadDq(); /* eslint-disable-next-line */ }, [client]);

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-5" data-testid="ask-asset-ai-tab">
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <h3 className="font-display text-base font-semibold text-slate-900">Ask the Capital Planning Adviser</h3>
          </div>
          <p className="text-xs text-slate-500 mt-1">Answers use only {client || "portfolio"} data. Facts → Interpretation → Risk → Recommendation → Capital Impact → Confidence → Next Action.</p>
          <div className="mt-3 flex gap-2">
            <Textarea rows={2} value={q} onChange={(e) => setQ(e.target.value)}
                      placeholder="e.g. If we only have $150k next year, what should we replace?"
                      data-testid="ask-ai-input" className="flex-1" />
            <Button className="bg-violet-600 hover:bg-violet-700 text-white self-end" onClick={() => ask(q)}
                    disabled={busy} data-testid="ask-ai-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {SAMPLES.map((s) => (
              <button key={s} onClick={() => { setQ(s); ask(s); }}
                      className="text-[11px] rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-600 hover:border-violet-300 hover:text-violet-700">
                {s}
              </button>
            ))}
          </div>
        </div>
        {busy && <div className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-500">Analysing {client || "portfolio"} data…</div>}
        {answer && (
          <div className="rounded-lg border border-violet-200 bg-white p-5" data-testid="ask-ai-answer">
            <Md text={answer.answer} />
          </div>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-5 h-fit" data-testid="data-quality-panel">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-gbe" />
          <h3 className="font-display text-sm font-semibold text-slate-900">Data Quality</h3>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] ml-auto" onClick={() => loadDq(true)} disabled={dqBusy}>
            {dqBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
        </div>
        {dq && (
          <>
            <div className="text-[11px] text-slate-500 mt-2">{fmtNumber(dq.total_assets)} assets · {dq.duplicate_name_site_groups} possible duplicate groups</div>
            <div className="mt-2 space-y-1.5">
              {dq.missing.slice(0, 6).map((m) => (
                <div key={m.field} className="text-xs">
                  <div className="flex justify-between text-slate-700"><span>{m.field}</span><span className="tabular-nums text-slate-500">{m.pct}% missing</span></div>
                  <div className="h-1 bg-slate-100 rounded-full mt-0.5"><div className="h-full bg-amber-400 rounded-full" style={{ width: `${m.pct}%` }} /></div>
                </div>
              ))}
            </div>
            {dq.priorities && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">Data improvement priorities</div>
                <Md text={dq.priorities} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
